import {domCreate} from "./helpers";

import "./CooperativeGestureControl.css";
import * as maplibregl from "maplibre-gl";
import {EventData, MapTouchEvent, MapWheelEvent} from "maplibre-gl";

const IS_MOBILE = /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent) || (
    ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(navigator.platform)
    || navigator.userAgent.includes("Mac") && "ontouchend" in document
)
const IS_MAC = ["MacIntel", "MacPPC", "Mac68K", "Macintosh", "MacARM"].includes(navigator.platform)

export class CooperativeGestureControl {
    protected _map?: maplibregl.Map;
    protected _container?: HTMLDivElement;
    private _message: HTMLDivElement;
    private _hideMessageTimeout?: number;
    private _activeTouch: boolean = false;
    private _activeTouchTime: number;

    constructor() {
        this.showMessage = this.showMessage.bind(this)
        this.hideMessage = this.hideMessage.bind(this)

        // Mobile Events
        this.onTouchMove = this.onTouchMove.bind(this)
        this.onTouchEnd = this.onTouchEnd.bind(this)
        this.onTouchStart = this.onTouchStart.bind(this)

        // Desktop
        this.onClear = this.onClear.bind(this)
        this.onWheel = this.onWheel.bind(this)
    }

    onAdd(map: maplibregl.Map) {
        this._map = map;

        this._message = domCreate('div', 'cooperative-gesture-control-map-message')
        this._message.textContent = CooperativeGestureControl._getMessage()
        this._map.getContainer().appendChild(this._message)

        map.touchZoomRotate.disableRotation()
        map.doubleClickZoom.enable()

        if (IS_MOBILE) {
            map.touchZoomRotate.enable()

            map.on("touchmove", this.onTouchMove)
            map.on("touchend", this.onTouchEnd)
            map.on("touchstart", this.onTouchStart)
        } else {
            map.touchZoomRotate.enable()
            map.dragPan.enable()
            map.scrollZoom.disable()
            map.scrollZoom.setWheelZoomRate(.02)

            map.on("dragstart", this.onClear)
            map.on("zoomstart", this.onClear)
            map.on("wheel", this.onWheel)
        }

        this._container = document.createElement('div');
        return this._container;
    }

    onRemove() {
        this._container.parentNode.removeChild(this._container);

        if (IS_MOBILE) {
            this._map.off("touchmove", this.onTouchMove)
            this._map.off("touchend", this.onTouchEnd)
            this._map.off("touchstart", this.onTouchStart)
        } else {
            this._map.off("dragstart", this.onClear)
            this._map.off("zoomstart", this.onClear)
            this._map.off("wheel", this.onWheel)
        }

        this._map = undefined;
    }

    showMessage() {
        this._message.style.transition = "opacity 0.5s"
        this._message.style.opacity = "0.999"
        clearTimeout(this._hideMessageTimeout)
        this._hideMessageTimeout = window.setTimeout(this.hideMessage, 1000)
    }

    hideMessage() {
        clearTimeout(this._hideMessageTimeout)
        this._message.style.opacity = "0"
    }

    protected onTouchMove(e: MapTouchEvent & EventData) {
        if (!this._activeTouch && e.originalEvent.touches && e.originalEvent.touches.length === 1) {
            this.showMessage()
        }
    }

    protected onTouchEnd() {
        setTimeout(() => {
            this._message.style.transition = "opacity 0.5s"
            if (this._map.dragPan.isEnabled()) {
                this._map.dragPan.disable()
            }
        }, 100)
    }

    protected onTouchStart(e: MapTouchEvent & EventData) {
        let origEvent = e.originalEvent
        if (origEvent && "touches" in origEvent && origEvent.touches.length > 1) {
            origEvent.stopImmediatePropagation()
            this.onClear()
            this._map.dragPan.enable()
            this._activeTouch = true
            this._activeTouchTime = Date.now()
        } else if (Date.now() - this._activeTouchTime >= 500) {
            if (this._map.dragPan.isEnabled()) {
                this._map.dragPan.disable()
            }
            this.showMessage()
            this._activeTouch = false
            this._activeTouchTime = 0
        } else {
            this.onClear()
        }
    }

    protected onClear() {
        this._message.style.transition = ""
        this.hideMessage()
    }

    protected onWheel(e: MapWheelEvent & EventData) {
        if (IS_MAC ? e.originalEvent.metaKey : e.originalEvent.ctrlKey) {
            e.originalEvent.preventDefault()
            this.onClear()
            this._map.scrollZoom.enable()
        } else {
            this.showMessage()
            if (this._map.scrollZoom.isEnabled()) {
                this._map.scrollZoom.disable()
            }
        }
    }

    private static _getMessage() {
        if (IS_MOBILE) {
            return 'Verschieben der Karte mit zwei Fingern'
        }
        if (IS_MAC) {
            return 'Halte die Taste "\u2318" beim Scrollen gedrückt, um die Karte zu vergrößern'
        }

        return 'Verwende Strg+Scrollen zum Zoomen der Karte';
    }
}
